import { useEffect, useState } from "react";
import { subDays } from "date-fns";
import { ArrowDownRight, ArrowUpRight, Inbox, Loader2, Minus } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { isoDay, scoreTone } from "@/lib/format";

export function PageHeader({ eyebrow, title, description, actions, testId }) {
  return (
    <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between fade-up" data-testid={testId}>
      <div>
        {eyebrow && <div className="eyebrow mb-1">{eyebrow}</div>}
        <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-white">{title}</h1>
        {description && <p className="text-sm text-slate-400 mt-1 max-w-2xl">{description}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

export function Delta({ value, suffix = "", invert = false, className }) {
  if (value == null || Number.isNaN(value)) return <span className={cn("text-xs text-slate-500 font-mono", className)}>—</span>;
  const good = invert ? value < 0 : value > 0;
  const Icon = value === 0 ? Minus : value > 0 ? ArrowUpRight : ArrowDownRight;
  return (
    <span className={cn("inline-flex items-center gap-0.5 text-xs font-mono", value === 0 ? "text-slate-500" : good ? "text-emerald-400" : "text-red-400", className)}>
      <Icon className="h-3 w-3" />
      {value > 0 ? "+" : ""}
      {Math.round(value * 10) / 10}
      {suffix}
    </span>
  );
}

export function KpiCard({ label, value, sub, delta, deltaSuffix, invert, tone = "blue", icon: Icon, testId, className }) {
  const tones = {
    blue: "text-blue-400 border-blue-500/30 bg-blue-500/10",
    cyan: "text-cyan-400 border-cyan-500/30 bg-cyan-500/10",
    emerald: "text-emerald-400 border-emerald-500/30 bg-emerald-500/10",
    amber: "text-amber-400 border-amber-500/30 bg-amber-500/10",
    red: "text-red-400 border-red-500/30 bg-red-500/10",
    violet: "text-violet-400 border-violet-500/30 bg-violet-500/10",
    slate: "text-slate-300 border-slate-500/30 bg-slate-500/10",
  };
  return (
    <div className={cn("panel panel-hover p-4 flex flex-col gap-3 fade-up", className)} data-testid={testId}>
      <div className="flex items-start justify-between gap-2">
        <div className="eyebrow">{label}</div>
        {Icon && (
          <span className={cn("h-7 w-7 rounded-md border inline-flex items-center justify-center", tones[tone])}>
            <Icon className="h-3.5 w-3.5" />
          </span>
        )}
      </div>
      <div className="flex items-end justify-between gap-2">
        <div className="metric text-2xl sm:text-3xl leading-none">{value}</div>
        {delta !== undefined && <Delta value={delta} suffix={deltaSuffix} invert={invert} />}
      </div>
      {sub && <div className="text-xs text-slate-500">{sub}</div>}
    </div>
  );
}

export function ScoreBadge({ score, size = "md", testId }) {
  const t = scoreTone(score);
  const sizes = { sm: "h-7 min-w-[2.25rem] text-xs px-1.5", md: "h-8 min-w-[2.75rem] text-sm px-2", lg: "h-12 min-w-[3.5rem] text-xl px-3" };
  return (
    <span data-testid={testId} className={cn("inline-flex items-center justify-center rounded-md border font-mono font-bold", t.text, t.bg, t.border, sizes[size])}>
      {score == null ? "—" : score}
    </span>
  );
}

export function Pill({ children, tone = "slate", className, testId }) {
  const tones = {
    slate: "text-slate-300 bg-slate-500/15 border-slate-500/30",
    blue: "text-blue-300 bg-blue-500/15 border-blue-500/30",
    cyan: "text-cyan-300 bg-cyan-500/15 border-cyan-500/30",
    emerald: "text-emerald-300 bg-emerald-500/15 border-emerald-500/30",
    amber: "text-amber-300 bg-amber-500/15 border-amber-500/30",
    red: "text-red-300 bg-red-500/15 border-red-500/30",
    violet: "text-violet-300 bg-violet-500/15 border-violet-500/30",
  };
  return (
    <span data-testid={testId} className={cn("inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[11px] font-mono uppercase tracking-wider font-semibold whitespace-nowrap", tones[tone] || tones.slate, className)}>
      {children}
    </span>
  );
}

export const sentimentTone = (s) => (s === "positive" ? "emerald" : s === "negative" ? "red" : "slate");
export const severityTone = (s) => (s === "high" ? "red" : s === "medium" ? "amber" : "blue");

export function EmptyState({ icon: Icon = Inbox, title, hint, action, testId }) {
  return (
    <div className="flex flex-col items-center justify-center text-center py-14 px-6 gap-2" data-testid={testId}>
      <span className="h-10 w-10 rounded-lg border border-[#2d3d58] bg-[#161f30] inline-flex items-center justify-center text-slate-400">
        <Icon className="h-5 w-5" />
      </span>
      <div className="text-sm font-semibold text-slate-200 mt-1">{title}</div>
      {hint && <div className="text-xs text-slate-500 max-w-sm">{hint}</div>}
      {action && <div className="mt-2">{action}</div>}
    </div>
  );
}

export function Spinner({ className }) {
  return <Loader2 className={cn("h-4 w-4 animate-spin text-slate-400", className)} />;
}

export function Loading({ label = "Loading" }) {
  return (
    <div className="flex items-center gap-2 text-sm text-slate-500 py-10 justify-center" data-testid="loading-indicator">
      <Spinner /> {label}…
    </div>
  );
}

export function Section({ title, eyebrow, actions, children, className, testId, padded = true }) {
  return (
    <section className={cn("panel fade-up min-w-0 overflow-hidden", className)} data-testid={testId}>
      {(title || actions) && (
        <header className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-[#1f293d]">
          <div>
            {eyebrow && <div className="eyebrow">{eyebrow}</div>}
            {title && <h2 className="text-sm sm:text-base font-bold text-slate-100">{title}</h2>}
          </div>
          {actions && <div className="flex items-center gap-2">{actions}</div>}
        </header>
      )}
      <div className={padded ? "p-4" : ""}>{children}</div>
    </section>
  );
}

const PRESETS = [
  { key: "7", label: "7d", days: 7 },
  { key: "14", label: "14d", days: 14 },
  { key: "30", label: "30d", days: 30 },
  { key: "90", label: "90d", days: 90 },
  { key: "365", label: "1y", days: 365 },
];

export function useRange(defaultDays = 30) {
  const stored = JSON.parse(localStorage.getItem("tqi_range") || "null");
  const [range, setRange] = useState(stored || { preset: String(defaultDays), from: isoDay(subDays(new Date(), defaultDays)), to: isoDay(new Date()) });
  useEffect(() => localStorage.setItem("tqi_range", JSON.stringify(range)), [range]);
  return [range, setRange];
}

export function RangePicker({ range, onChange }) {
  const pick = (p) => onChange({ preset: p.key, from: isoDay(subDays(new Date(), p.days)), to: isoDay(new Date()) });
  return (
    <div className="flex flex-wrap items-center gap-1.5" data-testid="range-picker">
      <div className="inline-flex rounded-md border border-[#1f293d] bg-[#0f172a] p-0.5">
        {PRESETS.map((p) => (
          <button
            key={p.key}
            data-testid={`range-preset-${p.key}`}
            onClick={() => pick(p)}
            className={cn("px-2.5 py-1 text-xs font-mono rounded transition-colors", range.preset === p.key ? "bg-blue-600 text-white" : "text-slate-400 hover:text-white hover:bg-[#161f30]")}
          >
            {p.label}
          </button>
        ))}
      </div>
      <Input data-testid="range-from-input" type="date" value={range.from} onChange={(e) => onChange({ preset: "custom", from: e.target.value, to: range.to })} className="h-7 w-[9.5rem] text-xs font-mono bg-[#0f172a]" />
      <span className="text-slate-600 text-xs">→</span>
      <Input data-testid="range-to-input" type="date" value={range.to} onChange={(e) => onChange({ preset: "custom", from: range.from, to: e.target.value })} className="h-7 w-[9.5rem] text-xs font-mono bg-[#0f172a]" />
    </div>
  );
}

export function FilterSelect({ value, onChange, options, placeholder, testId, className, allLabel = "All" }) {
  return (
    <Select value={value || "__all"} onValueChange={(v) => onChange(v === "__all" ? "" : v)}>
      <SelectTrigger data-testid={testId} className={cn("h-8 text-xs bg-[#0f172a] border-[#1f293d] min-w-[8rem]", className)}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent className="bg-[#111827] border-[#2d3d58]">
        <SelectItem value="__all" className="text-xs">
          {allLabel} {placeholder?.toLowerCase()}
        </SelectItem>
        {options.map((o) => (
          <SelectItem key={o.value} value={o.value} className="text-xs">
            {o.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export function Avatar({ name, url, size = "sm" }) {
  const initials = (name || "?")
    .split(" ")
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
  const cls = size === "sm" ? "h-7 w-7 text-[10px]" : "h-9 w-9 text-xs";
  return url ? (
    <img src={url} alt={name} className={cn("rounded-full object-cover border border-[#2d3d58]", cls)} />
  ) : (
    <span className={cn("rounded-full inline-flex items-center justify-center font-mono font-bold bg-[#161f30] border border-[#2d3d58] text-slate-300", cls)}>{initials}</span>
  );
}

export function LiveTimer({ since, className }) {
  const [, tick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => tick((x) => x + 1), 1000);
    return () => clearInterval(id);
  }, []);
  if (!since) return <span className={className}>—</span>;
  const s = Math.max(0, Math.floor((Date.now() - new Date(since).getTime()) / 1000));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const txt = h >= 48 ? `${Math.floor(h / 24)}d ${h % 24}h` : `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}:${sec.toString().padStart(2, "0")}`;
  return <span className={cn("font-mono tabular-nums", className)}>{txt}</span>;
}
